import { TimePeriod } from "../../hooks/useDetectTimePeriod";
import { SelectOptionType } from "../../types/types";

export interface InvoiceTime {
  duration: number;
  isBilled: boolean;
  /** false = not charged to the client (missing on older data = billable). */
  isBillable?: boolean;
  notes: string;
  contactId: string;
  contact: any;
  businessContactId: any;
  businessContact: any;
  workId: string;
  work: Work;
  jobTaskId: any;
  workTask: any;
  workTypeId: any;
  workType: any;
  assignedUserId: string;
  assignedUserRelayId: any;
  assignedUser: AssignedUser2 | null;
  date: string;
  timerStatus: string;
  dateString: string;
  id: string;
  relayId: any;
  createdAt: string;
  createdTimeUTC: any;
  editedTimeUTC: any;
  dueDate: string;
  workName: string;
}

export interface Time {
  filterByDate: TimePeriod[];
  filterByWorkType: SelectOptionType[];
  filterByClient: SelectOptionType[];
  filterByStatus: boolean | null;
  /** true = billable only, false = non-billable only. */
  filterByBillable?: boolean | null;
  filterByAssignee: SelectOptionType[];
  fromDate: Date | null;
  toDate: Date | null;
  orgId: any;
  sortOrder: string;
  sortBy: string;
}
export interface Work {
  name: string;
  description: string;
  isReassigned: boolean;
  assignedByUserId: any;
  recurringWorkId: any;
  stopRecurring: boolean;
  startDate: string;
  dueDate: string;
  assignedUserId: string;
  serviceId: string;
  orgId: any;
  orgRelayId: any;
  jobStatusId: string;
  jobSubStatusId: any;
  statusUpdatedAt: string;
  isArchived: boolean;
  customerId: string;
  businessContactId: any;
  repeatTypeEnumId: string;
  repeatOnId: any;
  beforeDays: number;
  repeatValue: number;
  beforeStartDate: number;
  beforeStartDateValue: string;
  nextRecurringDate: string;
  notifyMe: boolean;
  repeatDueDate: any;
  repeatStartDate: any;
  repeatUntil: string;
  templateId: string;
  template: any;
  repeatOn: any;
  repeatTypeEnum: any;
  workTasks: any[];
  workComments: any[];
  assignedUser: AssignedUser;
  service: Service;
  jobStatus: any;
  jobSubStatus: any;
  customer: Customer;
  businessContact: any;
  workNotes: any[];
  openWork: number;
  overDueWork: number;
  workCompleted: number;
  duration: any;
  totalRecords: any;
  jobTaskCounts: number;
  taskCountsByUser: number;
  id: string;
  relayId: any;
  createdAt: string;
  createdTimeUTC: any;
  editedTimeUTC: any;
}

export interface AssignedUser {
  orgRelayId: any;
  userRelayId: any;
  receiveEmailNotification: boolean;
  sendNotificationToEmail: string;
  syncEmails: boolean;
  orgId: string;
  userRoleEnumId: string;
  hourlyRate: number;
  statusId: string;
  statusType: any;
  azureB2CUserId: string;
  azureB2CTempPass: any;
  invitedAt: string;
  invitationCount: number;
  nylasToken: any;
  userProfile: UserProfile;
  userAgreement: any;
  org: any;
  userRoleEnum: any;
  userFeatureFlag: any;
  userPermission: any[];
  userAssignedClients: any[];
  permissionIds: any;
  assignedCustomerIds: any;
  assignedAllClients: boolean;
  id: string;
  relayId: any;
  createdAt: string;
  createdTimeUTC: any;
  editedTimeUTC: any;
}

export interface UserProfile {
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  userId: string;
  id: string;
  relayId: any;
  createdAt: string;
  createdTimeUTC: any;
  editedTimeUTC: any;
}

export interface Service {
  name: string;
  description: any;
  locationId: string;
  locationRelayId: any;
  serviceStatuses: any[];
  serviceFee: any;
  location: any;
  id: string;
  relayId: any;
  createdAt: string;
  createdTimeUTC: any;
  editedTimeUTC: any;
}

export interface Customer {
  name: string;
  customerTypeEnumId: string;
  contactId: string;
  businessContactId: any;
  locationId: string;
  addresses: any[];
  contact: any;
  businessContact: any;
  customerTag: any[];
  openWorkCounts: number;
  id: string;
  relayId: any;
  createdAt: string;
  createdTimeUTC: any;
  editedTimeUTC: any;
}

export interface AssignedUser2 {
  orgRelayId: any;
  userRelayId: any;
  receiveEmailNotification: boolean;
  sendNotificationToEmail: string;
  syncEmails: boolean;
  orgId: string;
  userRoleEnumId: string;
  hourlyRate: number;
  statusId: string;
  statusType: any;
  azureB2CUserId: string;
  azureB2CTempPass: any;
  invitedAt: string;
  invitationCount: number;
  nylasToken: any;
  userProfile: UserProfile2;
  userAgreement: any;
  org: any;
  userRoleEnum: any;
  userFeatureFlag: any;
  userPermission: any[];
  userAssignedClients: any[];
  permissionIds: any;
  assignedCustomerIds: any;
  assignedAllClients: boolean;
  id: string;
  relayId: any;
  createdAt: string;
  createdTimeUTC: any;
  editedTimeUTC: any;
}

export interface UserProfile2 {
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  userId: string;
  id: string;
  relayId: any;
  createdAt: string;
  createdTimeUTC: any;
  editedTimeUTC: any;
}

/**
 * Time that may still go on an invoice: billable (missing = billable) and not
 * billed yet. Already-billed time was offered again before 2026-10-22 (QA C1),
 * so a client could be billed twice for the same hours.
 */
export const isInvoiceableTime = (
  time: Pick<InvoiceTime, "isBillable" | "isBilled"> | null | undefined,
) => !!time && time.isBillable !== false && time.isBilled !== true;

export const invoiceableTimes = <
  T extends Pick<InvoiceTime, "isBillable" | "isBilled">,
>(
  times: T[] | null | undefined,
) => (times ?? []).filter(isInvoiceableTime);
